import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  FaLinkedin,
  FaGithub,
  FaTwitter,
  FaMedium,
  FaEnvelope,
  FaArrowRight,
} from "react-icons/fa";
import { HiOutlineExternalLink } from "react-icons/hi";
import profileImage from "../../assets/images/profile.jpg";

const HeroSection = () => {
  const { scrollY } = useScroll();
  const backgroundY = useTransform(scrollY, [0, 500], [0, 150]);
  const textY = useTransform(scrollY, [0, 300], [0, -50]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <motion.section
      className="relative min-h-screen overflow-hidden bg-gradient-to-br from-vxdf-dark via-brand-dark to-vxdf-primary -mt-16 pt-16 md:pt-0"
      style={{ y: backgroundY }}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-hero-pattern opacity-10"></div>
        <motion.div
          className="absolute top-20 left-10 w-72 h-72 bg-vxdf-accent/20 rounded-full blur-3xl"
          animate={{
            x: mousePosition.x * 0.02,
            y: mousePosition.y * 0.02,
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-brand-secondary/20 rounded-full blur-3xl"
          animate={{
            x: mousePosition.x * -0.02,
            y: mousePosition.y * -0.02,
          }}
        />
      </div>

      <div className="relative z-10 container mx-auto px-4 min-h-screen flex items-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          style={{ y: textY, opacity }}
          className="w-full"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="text-white">
              {/* VXDF Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-6"
              >
                <span className="vxdf-badge">
                  <span className="mr-2">🚀</span>
                  Leading the OWASP VXDF Standard
                </span>
              </motion.div>

              {/* Name and Title */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-responsive-5xl mb-4"
              >
                <span className="font-light">Hi, I'm</span>
                <br />
                <span className="font-black gradient-text">Mihir Shah</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-responsive-xl text-gray-300 mb-6 leading-relaxed"
              >
                Security Leader specializing in{" "}
                <span className="text-white">Cloud</span> &{" "}
                <span className="text-white">Application Security</span>
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-responsive-base text-gray-400 mb-8 leading-relaxed"
              >
                <span className="text-vxdf-accent font-semibold">
                  Project Leader
                </span>{" "}
                for the
                <span className="text-white font-semibold">
                  {" "}
                  OWASP VXDF
                </span>{" "}
                (Validated Exploitable Data Flow) standard, helping the security
                community better document and share vulnerability data. Author
                of the{" "}
                <span className="text-white font-semibold">
                  Cloud Native Software Security Handbook
                </span>
                .
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-wrap gap-4 mb-8"
              >
                <motion.a
                  href="https://vxdf.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-vxdf group"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="flex items-center">
                    Explore VXDF
                    <HiOutlineExternalLink className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.a>
                <motion.a
                  href="/projects"
                  className="btn-secondary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View My Work
                </motion.a>
              </motion.div>

              {/* Social Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex space-x-4"
              >
                {[
                  {
                    icon: FaLinkedin,
                    href: "https://linkedin.com/in/mihir-shah99",
                    label: "LinkedIn",
                  },
                  {
                    icon: FaGithub,
                    href: "https://github.com/mihir-shah99",
                    label: "GitHub",
                  },
                  {
                    icon: FaTwitter,
                    href: "https://x.com/mihirshah99",
                    label: "Twitter",
                  },
                  {
                    icon: FaMedium,
                    href: "https://mihirshah99.medium.com",
                    label: "Medium",
                  },
                  {
                    icon: FaEnvelope,
                    href: "mailto:mihir@mihirshah.tech",
                    label: "Email",
                  },
                ].map((social, index) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-vxdf-secondary hover:border-vxdf-accent transition-all duration-300"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8 + index * 0.1 }}
                  >
                    <social.icon size={20} />
                  </motion.a>
                ))}
              </motion.div>
            </div>

            {/* Right Column - Profile and VXDF Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="relative px-8 lg:px-12"
            >
              {/* Profile Image with VXDF Glow */}
              <div className="relative mx-auto w-80 h-80 lg:w-96 lg:h-96 overflow-visible">
                {/* Animated rings */}
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-vxdf-accent/30"
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                />
                <motion.div
                  className="absolute inset-4 rounded-full border-2 border-vxdf-secondary/30"
                  animate={{ rotate: -360 }}
                  transition={{
                    duration: 15,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                />

                {/* Profile image container */}
                <div className="absolute inset-8 rounded-full overflow-hidden border-4 border-white/20 shadow-2xl">
                  <img
                    src={profileImage}
                    alt="Mihir Shah"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-vxdf-primary/50 to-transparent"></div>
                </div>

                {/* VXDF Logo/Text Overlay */}
                <motion.div
                  className="absolute -bottom-4 -right-4 bg-vxdf-gradient rounded-xl px-6 py-3 shadow-glow"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <div className="text-white font-bold text-xl">OWASP VXDF</div>
                  <div className="text-white/80 text-xs">Project Leader</div>
                </motion.div>

                {/* Floating tech badges - now inside the profile container */}
                {["AppSec", "Cloud Security", "DevSecOps", "OWASP"].map(
                  (tech, index) => {
                    // Calculate positions in a circle around the profile
                    const angles = [30, 120, 210, 300]; // More spread out angles
                    const angle = angles[index];
                    const radius = 75; // Further increased radius
                    const x = 50 + radius * Math.cos((angle * Math.PI) / 180);
                    const y = 50 + radius * Math.sin((angle * Math.PI) / 180);

                    return (
                      <motion.div
                        key={tech}
                        className="absolute bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-white text-sm border border-white/20 whitespace-nowrap shadow-lg z-10"
                        style={{
                          left: `${x}%`,
                          top: `${y}%`,
                          transform: "translate(-50%, -50%)",
                        }}
                        animate={{
                          scale: [1, 1.05, 1],
                          y: [0, -5, 0],
                        }}
                        transition={{
                          duration: 4 + index * 0.5,
                          repeat: Infinity,
                          delay: index * 0.3,
                          ease: "easeInOut",
                        }}
                      >
                        {tech}
                      </motion.div>
                    );
                  },
                )}
              </div>
            </motion.div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="text-white/50 text-center"
            >
              <div className="text-sm mb-2">Scroll to explore</div>
              <FaArrowRight className="rotate-90 mx-auto" />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default HeroSection;
